
import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const EnvironmentalSustainabilityPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="mb-12">
            <Link 
              to="/" 
              className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-6 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Environmental & Sustainability Policy</h1>
            <p className="text-lg text-gray-600">
              Our commitment to minimising environmental impact and operating responsibly
            </p>
          </div>

          {/* Policy Content */}
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <section className="mb-12">
              <p className="text-gray-700 leading-relaxed mb-6">
                Lewis Security, Risk & Compliance Solutions is committed to minimising environmental impact and operating responsibly within the communities we serve.
              </p>
            </section>

            {/* Our Commitments */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Commitments</h2>
              <div className="bg-teal-50 rounded-lg p-8 mb-6">
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>Reducing unnecessary travel</strong> and planning efficient site routes</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>Offering remote consultancy</strong> and online training where suitable</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>Recycling packaging and materials</strong> wherever practicable</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>Using accredited, qualified, and responsible partners</strong> for specialist work</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>Reducing waste</strong> and encouraging clients to adopt sustainable solutions</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>Complying with UK Environmental Legislation</strong></span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Sustainable Solutions */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Environmentally Friendly Solutions</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Where possible, we support environmentally friendly installation options such as:
              </p>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white border-2 border-teal-100 rounded-lg p-6 hover:border-teal-300 transition-colors">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-lightbulb-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">LED & Low-Energy Systems</h3>
                  <p className="text-gray-600 text-sm">Energy-efficient lighting and power solutions</p>
                </div>
                <div className="bg-white border-2 border-teal-100 rounded-lg p-6 hover:border-teal-300 transition-colors">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-sun-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Solar-Based Solutions</h3>
                  <p className="text-gray-600 text-sm">Renewable energy through specialist partners</p>
                </div>
                <div className="bg-white border-2 border-teal-100 rounded-lg p-6 hover:border-teal-300 transition-colors">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-battery-charge-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Battery Storage</h3>
                  <p className="text-gray-600 text-sm">Renewable technologies and storage systems</p>
                </div>
              </div>
            </section>

            {/* Travel & Operations */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Travel & Operations</h2>
              <div className="bg-gray-50 rounded-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Efficient Route Planning</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We plan site visits and training delivery to minimise unnecessary travel, combining appointments where possible and using the most efficient routes.
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-4 mt-6">Remote Services</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Where appropriate, we offer remote consultancy, virtual risk assessments, and online training to reduce carbon emissions and travel time.
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-4 mt-6">Digital Documentation</h3>
                <p className="text-gray-700 leading-relaxed">
                  We prioritise digital documentation and electronic communication to reduce paper usage and waste.
                </p>
              </div>
            </section>

            {/* Waste Management */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Waste Management & Recycling</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                We are committed to responsible waste management:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-recycle-line text-teal-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Recycling packaging materials and equipment wherever practicable</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-recycle-line text-teal-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Proper disposal of electronic waste through certified partners</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-recycle-line text-teal-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Minimising single-use plastics in training and operations</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-recycle-line text-teal-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Encouraging clients to recycle old security equipment responsibly</span>
                </li>
              </ul>
            </section>

            {/* Supply Chain */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Responsible Supply Chain</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                We work with partners and suppliers who share our environmental values:
              </p>
              <div className="bg-teal-50 rounded-lg p-8">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-shield-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Selecting accredited and certified partners for specialist installations</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-shield-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Prioritising suppliers with environmental credentials</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-shield-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Encouraging sustainable practices throughout our supply chain</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-shield-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Supporting local suppliers to reduce transportation emissions</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Client Support */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Supporting Client Sustainability</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                We help clients make environmentally responsible choices:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-leaf-line text-teal-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Recommending energy-efficient security and fire safety systems</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-leaf-line text-teal-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Advising on sustainable installation options</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-leaf-line text-teal-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Providing guidance on reducing operational environmental impact</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-leaf-line text-teal-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Sharing best practices for sustainable security operations</span>
                </li>
              </ul>
            </section>

            {/* Compliance */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Environmental Compliance</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                We comply with all relevant UK environmental legislation, including:
              </p>
              <div className="bg-gray-50 rounded-lg p-8">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-file-list-3-line text-gray-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Environmental Protection Act 1990</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-file-list-3-line text-gray-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Waste Electrical and Electronic Equipment (WEEE) Regulations</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-file-list-3-line text-gray-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Climate Change Act 2008</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-file-list-3-line text-gray-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Environment Act 2021</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Continuous Improvement */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Continuous Improvement</h2>
              <div className="bg-teal-50 rounded-lg p-8">
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>We aim to continuously improve our environmental performance year on year.</strong>
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This includes:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Regular review of our environmental practices</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Setting measurable environmental targets</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Monitoring our carbon footprint and waste reduction</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Staying informed about new sustainable technologies</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Training staff and partners on environmental best practices</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Contact */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Questions or Suggestions</h2>
              <div className="bg-gray-50 rounded-lg p-8">
                <p className="text-gray-700 leading-relaxed mb-6">
                  If you have questions about our environmental policy or suggestions for improvement, please contact us:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-teal-600 text-xl mr-3"></i>
                    <a href="mailto:info@lewissecurity.co.uk" className="text-teal-600 hover:text-teal-700 cursor-pointer">
                      info@lewissecurity.co.uk
                    </a>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-phone-line text-teal-600 text-xl mr-3"></i>
                    <a href="tel:+447951477416" className="text-teal-600 hover:text-teal-700 cursor-pointer">
                      +44 7951 477416
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* Useful Resources */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Useful Resources</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Environment Agency</h3>
                  <p className="text-gray-600 text-sm mb-3">UK environmental regulation and guidance</p>
                  <a 
                    href="https://www.gov.uk/government/organisations/environment-agency" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-700 text-sm cursor-pointer inline-flex items-center"
                  >
                    Visit Website <i className="ri-external-link-line ml-1"></i>
                  </a>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Carbon Trust</h3>
                  <p className="text-gray-600 text-sm mb-3">Carbon reduction and sustainability advice</p>
                  <a 
                    href="https://www.carbontrust.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-700 text-sm cursor-pointer inline-flex items-center"
                  >
                    Visit Website <i className="ri-external-link-line ml-1"></i>
                  </a>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">WEEE Regulations</h3>
                  <p className="text-gray-600 text-sm mb-3">Electronic waste disposal guidance</p>
                  <a 
                    href="https://www.gov.uk/electricalwaste-producer-supplier-responsibilities" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-700 text-sm cursor-pointer inline-flex items-center"
                  >
                    Visit Website <i className="ri-external-link-line ml-1"></i>
                  </a>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Business Energy Advice</h3>
                  <p className="text-gray-600 text-sm mb-3">Energy efficiency for businesses</p>
                  <a 
                    href="https://www.gov.uk/guidance/energy-technology-list" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-700 text-sm cursor-pointer inline-flex items-center"
                  >
                    Visit Website <i className="ri-external-link-line ml-1"></i>
                  </a>
                </div>
              </div>
            </section>

            {/* Last Updated */}
            <section className="border-t border-gray-200 pt-8">
              <p className="text-sm text-gray-500">
                <strong>Last updated:</strong> 9 December 2025
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EnvironmentalSustainabilityPolicy;
